/**
 * Per-service commission rates (advanced setup) — form-boundary helpers.
 *
 * While editing, rates are held as display strings keyed by salon service id
 * (`{ [serviceId]: "40%" | "$10" }`), the same "15%" / "$25" idiom
 * `CommissionInput` uses for the single service rate. These helpers convert
 * to/from the wire `ServiceCommissionOverride[]` and summarise a record's
 * rates for display.
 */
import { parseCommissionValue } from "../components/settings/CommissionInput";
import {
  CommissionRecord,
  ServiceCommissionOverride,
} from "../types/commission";
import { formatCommissionAmount } from "./commissionUtils";

/** Display-string rates keyed by salon service id. */
export type ServiceOverrideDrafts = Record<string, string>;

/** Wire overrides → editable drafts (e.g. `{ svc1: "40%", svc2: "$10" }`). */
export const overridesToDrafts = (
  overrides: ServiceCommissionOverride[] | undefined | null,
): ServiceOverrideDrafts =>
  Object.fromEntries(
    (overrides || []).map((o) => [
      o.salon_service_id,
      formatCommissionAmount(o.amount, o.amount_type) || "",
    ]),
  );

/** A bare number means a percentage (matches CommissionInput's blur rule). */
const normalizeDraft = (value: string): string => {
  const trimmed = value.trim();
  return /^\d+(\.\d+)?$/.test(trimmed) ? `${trimmed}%` : trimmed;
};

export interface DraftsToOverridesResult {
  overrides: ServiceCommissionOverride[];
  /** The first invalid row's message, or null when every rate parses. */
  error: string | null;
}

/**
 * Editable drafts → wire overrides. `serviceNames` labels error messages.
 * An empty box — or a 0 rate — is "no record": in service-specific mode the
 * row's default service rate is 0%, so those services earn nothing.
 */
export const draftsToOverrides = (
  drafts: ServiceOverrideDrafts,
  serviceNames: Record<string, string> = {},
): DraftsToOverridesResult => {
  const overrides: ServiceCommissionOverride[] = [];
  for (const [serviceId, raw] of Object.entries(drafts)) {
    if (!raw || !raw.trim()) continue;
    const parsed = parseCommissionValue(normalizeDraft(raw));
    if (!parsed) {
      const name = serviceNames[serviceId] || "each service";
      return {
        overrides: [],
        error: `Enter a valid rate for ${name} (e.g. 15% or $25).`,
      };
    }
    if (parsed.amount === 0) continue;
    overrides.push({
      salon_service_id: serviceId,
      amount: parsed.amount,
      amount_type: parsed.amountType,
    });
  }
  return { overrides, error: null };
};

/** How many per-service rates a record carries (0 for TIERED / none). */
export const countServiceOverrides = (
  record: Pick<CommissionRecord, "service_overrides"> | null | undefined,
): number => record?.service_overrides?.length ?? 0;

/** One line per custom rate, e.g. "Color: 40%" — for tooltips. */
export const describeServiceOverrides = (
  overrides: ServiceCommissionOverride[] | undefined | null,
): string[] =>
  (overrides || []).map((o) => {
    const name = o.salon_service_name || "Service";
    const rate = formatCommissionAmount(o.amount, o.amount_type) || "—";
    return o.is_active === false
      ? `${name} (inactive): ${rate}`
      : `${name}: ${rate}`;
  });

/**
 * The services that earn something in the drafts, A–Z, e.g.
 * `["Balayage 50%", "Root Touch-up $25"]` — the form's summary preview.
 */
export const describeDraftRates = (
  drafts: ServiceOverrideDrafts,
  serviceNames: Record<string, string> = {},
): string[] =>
  draftsToOverrides(drafts)
    .overrides.map((o) => ({
      name: serviceNames[o.salon_service_id] || "Service",
      rate: formatCommissionAmount(o.amount, o.amount_type) || "",
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(({ name, rate }) => `${name} ${rate}`);
